import React, { useMemo, useEffect, MouseEvent } from 'react'
import ReportTable from '../view/common/ReportTable'
import { useRefetchableFragment, graphql } from 'react-relay/hooks'
import { TableConnected, Button } from 'elements'
import { getTransactionAnomalyCounts } from '../utils/resolutions/data'
import { ResolutionStatus } from '../types'
import ResolutionRowSubTable from './ResolutionRowSubTable'
import { reportResolutionColumns } from './constants'
import TableEmptyState from '../../common/TableEmptyState'
import { usePage } from 'hooks'
import { useHistory } from 'react-router-dom'
import { getRowRange } from 'utils/table'

const { CheckboxCell } = TableConnected as any

type Props = {
  report: any
  resolutionSection: ResolutionStatus
  anomalyView: string
  onRowSelect: Function
}

function createSelectColumn() {
  let lastSelectedId = ''

  return {
    accessor: 'select',
    Header: ({ getToggleAllRowsSelectedProps }: any) => (
      <CheckboxCell {...getToggleAllRowsSelectedProps()} />
    ),
    Cell: ({ row, rows, rowsById }: any) => {
      return (
        <CheckboxCell
          {...row.getToggleRowSelectedProps()}
          // allow shift+select to select row ranges
          onClick={(e: MouseEvent<HTMLInputElement>): void => {
            if (e.shiftKey) {
              const rowsToToggle = getRowRange(rows, row.id, lastSelectedId)
              const isCellSelected = rowsById[row.id].isSelected
              rowsToToggle.forEach((r) => r.toggleRowSelected(!isCellSelected))
            } else {
              row.toggleRowSelected()
            }

            lastSelectedId = row.id
          }}
        />
      )
    },
    disableSortBy: true,
    width: '10px',
    noTruncate: true,
  }
}

export default function ReportResolutionTable(props: Props) {
  const { report, resolutionSection, anomalyView, onRowSelect } = props
  const history = useHistory()
  const { pageProps } = usePage()

  const [data, refetch] = useRefetchableFragment(
    graphql`
      fragment ReportResolutionTable_report on Report
        @refetchable(queryName: "ReportResolutionTableRefetchQuery") {
        filteredReportResolutions(input: { anomalies: $anomalies, status: $resolutionSection }) {
          nodes {
            id
            anomalyTypes
            currentValue
            status
            updateStatus
            responseData
            resolutionKind
          }
        }
      }
    `,
    report,
  )

  const resolutions = data.filteredReportResolutions.nodes

  useEffect(() => {
    refetch(
      { anomalies: anomalyView === 'all' ? null : [anomalyView], resolutionSection },
      { fetchPolicy: 'store-and-network' },
    )
  }, [anomalyView, resolutionSection, refetch])

  const dataSection = useMemo(() => {
    const tableOptions = {
      renderRowSubComponent: (row: any) => {
        return <ResolutionRowSubTable row={row} reportId={data.id} anomalyView={anomalyView} />
      },
      onRowSelect,
      pagination: true,
      pageSize: 25,
      rowProps: (row: any) => {
        const { resolution } = row.original
        const { status, updateStatus } = resolution

        const props: any = {}

        if (status === 'forReview' && updateStatus === 'error') {
          props.border = 'solid 3px'
          props.borderColor = 'danger.0'
        }

        return props
      },
      initialSortColumns: [{ id: 'transactionDate' }, { id: 'anomalyCounts', desc: true }],
    }

    const columns = [...reportResolutionColumns]

    if (resolutionSection === 'forReview') {
      columns.unshift(createSelectColumn())
    }

    const resolutionData = resolutions.map((resolution: any) => ({
      ...resolution.currentValue.transactionData,
      anomalyCounts: getTransactionAnomalyCounts(resolution.anomalyTypes),
      anomalyTypes: resolution.anomalyTypes,
      // pass through entire resolution for the modal to use
      resolution,
    }))

    return {
      data: resolutionData,
      columns,
      onRowClick: (row: any, rows: any[]) => {
        rows.forEach((listRow) => {
          // exclude current row or it will stay expanded on click
          if (listRow.isExpanded && listRow.id !== row.id) {
            listRow.toggleRowExpanded()
          }
        })

        row.toggleRowExpanded()
      },
      tableOptions,
    }
  }, [resolutionSection, anomalyView, data.id, resolutions, onRowSelect])

  const sectionEmptyStates: { [key: string]: any } = {
    forReview: (
      <TableEmptyState
        message={
          pageProps?.progress === 100
            ? 'Success! All anomalies have been reviewed.'
            : 'All anomalies in this section have been reviewed.'
        }
        action={
          pageProps?.progress === 100 && (
            <Button onClick={() => history.push('transaction-insights')}>View insights</Button>
          )
        }
        styleProps={{ color: 'primary.0', fontSize: 'xxlarge' }}
      />
    ),
    reviewed: (
      <TableEmptyState
        message="No transactions have been marked as reviewed."
        styleProps={{ fontSize: 'xxlarge' }}
      />
    ),
    resolved: (
      <TableEmptyState
        message="No transactions have been resolved."
        styleProps={{ fontSize: 'xxlarge' }}
      />
    ),
  }

  if (resolutions.length) {
    return <ReportTable dataSection={dataSection} />
  }

  return sectionEmptyStates[resolutionSection]
}
